import { i18n } from "@dehub/theme"
import { IBranch } from "../models/Branch"
import { IWarehouseAddress } from "../models/DeliveryNote"
import { extractDistrictName } from "./district"

// A bare 4-6 digit token is a postal/zip code, not a human-readable place name.
const POSTAL_CODE = /^\d{4,6}$/

const isPostalCode = (s: string): boolean => POSTAL_CODE.test(s)

// Shared structured-address fields, plus a free-text fallback line.
interface AddressLike {
  province?: string | null
  district?: string | null
  khoroo?: string | null
  khoroolol?: string | null
  khotkhonBair?: string | null
  khashaaDavkhar?: string | null
  khaalgaDugaar?: string | null
  freeText?: string | null
}

// Compose a display address from structured fields, falling back to the
// free-text line. `province`/`district` frequently hold a postal code (e.g.
// "14000") instead of a real place name, which is meaningless to the operator;
// drop any part that is just a postal code so it never leaks into the line.
//
// `extraDistrictText` is an optional secondary source (e.g. the receiver
// branch's free-text) used only to recover the дүүрэг when this address's own
// district field is a postal code and its free-text doesn't name a district.
function composeAddress(a: AddressLike, extraDistrictText?: string | null): string | null {
  const structuredDistrict = typeof a.district === "string" ? a.district.trim() : ""
  // Usable only when it's a real name, not the postal code these fields usually
  // hold (e.g. "14000").
  const usableDistrict =
    structuredDistrict.length > 0 && !isPostalCode(structuredDistrict) ? structuredDistrict : null

  const parts = [
    a.province,
    usableDistrict,
    a.khoroo ? i18n.t("common:address.khoroo", { n: a.khoroo }) : null,
    a.khoroolol,
    a.khotkhonBair,
    a.khashaaDavkhar,
    a.khaalgaDugaar,
  ]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter((s) => s.length > 0 && !isPostalCode(s))

  if (parts.length > 0) {
    // Structured district was missing/a postal code, but there are other
    // structured parts: recover the дүүрэг from this line's free-text, then from
    // the secondary source (e.g. the branch), and prepend it so the operator
    // sees which district the delivery is in.
    if (!usableDistrict) {
      const recovered = extractDistrictName(a.freeText) ?? extractDistrictName(extraDistrictText)
      if (recovered) parts.unshift(i18n.t("common:address.district", { name: recovered }))
    }
    return parts.join(", ")
  }
  // No structured parts — show the full free-text line untouched.
  if (a.freeText?.trim()) return a.freeText.trim()
  return null
}

export function formatBranchAddress(branch: IBranch | null | undefined): string | null {
  if (!branch) return null
  return composeAddress({ ...branch, freeText: branch.branchAddress })
}

export function formatWarehouseAddress(
  wh: IWarehouseAddress | null | undefined,
  // Optional branch whose district/free-text recovers the дүүрэг when the
  // warehouse's own district is a postal code and its free-text lacks one.
  branchForDistrict?: IBranch | null,
): string | null {
  if (!wh) return null
  const extraDistrictText = branchForDistrict
    ? [branchForDistrict.district, branchForDistrict.branchAddress].filter(Boolean).join(" ")
    : null
  return composeAddress({ ...wh, freeText: wh.warehouseAddress }, extraDistrictText)
}

// Delivery destination address. The receiver's warehouse carries the readable
// address, so prefer it; fall back to the branch when there's no warehouse.
// The warehouse's district field is usually a postal code, so pass the branch
// through so its free-text/district can supply the readable дүүрэг.
export function formatReceiverAddress(
  branch: IBranch | null | undefined,
  warehouse: IWarehouseAddress | null | undefined,
): string | null {
  return formatWarehouseAddress(warehouse, branch) ?? formatBranchAddress(branch)
}
